import React, { useEffect, useState, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { LayoutDashboard, Package, Receipt, Users, Loader2, Pencil, Trash2, Plus, Tag, Megaphone, Download, Upload, Trophy, Printer } from "lucide-react";

import { api, fmtPrice } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Switch } from "../components/ui/switch";
import { Badge } from "../components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "../components/ui/alert-dialog";
import DiscountsTab from "../components/admin/DiscountsTab";
import AnnouncementTab from "../components/admin/AnnouncementTab";
import PartnersTab from "../components/admin/PartnersTab";
import { openPrintLabel, openPrintAllLabels } from "../lib/printLabel";

const STATUS_ORDER = ["pending", "processing", "shipped", "delivered", "cancelled"];
const STATUS_COLOR = {
  pending: "bg-white/10 text-[#A8A8A8]",
  processing: "bg-[#4ADE80]/20 text-[#4ADE80]",
  shipped: "bg-blue-500/20 text-blue-300",
  delivered: "bg-emerald-500/20 text-emerald-300",
  cancelled: "bg-red-500/20 text-red-300",
};

function StatCard({ label, value, sub }) {
  return (
    <div data-testid={`stat-${label.toLowerCase().replace(/\s+/g, "-")}`} className="border border-white/10 bg-[#1F1F1F] p-6">
      <div className="text-[10px] uppercase tracking-[0.2em] text-[#A8A8A8]">{label}</div>
      <div className="mt-3 font-display text-3xl text-[#F5F5F5]">{value}</div>
      {sub && <div className="mt-2 text-xs text-[#A8A8A8]">{sub}</div>}
    </div>
  );
}

function DashboardTab({ stats, loading }) {
  if (loading) return <div className="flex justify-center py-16"><Loader2 className="animate-spin text-[#4ADE80]" /></div>;
  if (!stats) return null;
  const rev = stats.revenue_by_currency || [];
  return (
    <div className="space-y-8">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <StatCard label="Products" value={stats.total_products} />
        <StatCard label="Users" value={stats.total_users} />
        <StatCard label="Paid Orders" value={stats.paid_orders} />
        <StatCard label="Pending E-transfers" value={stats.pending_etransfers} />
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="border border-white/10 bg-[#1F1F1F] p-6">
          <div className="text-[10px] uppercase tracking-[0.2em] text-[#A8A8A8]">Revenue</div>
          <div className="mt-4 space-y-2">
            {rev.length === 0 && <div className="text-sm text-[#A8A8A8]">No paid revenue yet.</div>}
            {rev.map((r) => (
              <div key={r._id} className="flex justify-between text-sm">
                <span className="text-[#A8A8A8]">{(r._id || "usd").toUpperCase()}</span>
                <span className="font-display text-lg text-[#F5F5F5]">{Number(r.revenue).toFixed(2)} <span className="text-xs text-[#A8A8A8]">({r.orders} orders)</span></span>
              </div>
            ))}
          </div>
        </div>
        <div className="border border-white/10 bg-[#1F1F1F] p-6">
          <div className="text-[10px] uppercase tracking-[0.2em] text-[#A8A8A8]">Low Stock (≤ 5)</div>
          <div className="mt-4 space-y-2">
            {(stats.low_stock || []).length === 0 && <div className="text-sm text-[#A8A8A8]">All products well-stocked.</div>}
            {(stats.low_stock || []).map((p) => (
              <div key={p.slug} className="flex justify-between text-sm">
                <span className="text-[#F5F5F5]">{p.name}</span>
                <span className={`font-mono ${p.stock === 0 ? "text-red-400" : "text-[#4ADE80]"}`}>{p.stock} left</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

const EMPTY_PRODUCT = {
  slug: "", name: "", category: "", tagline: "", description: "",
  sequence: "", molecular_weight: "", purity: "", price: 0,
  size: "", image: "", featured: false, stock: 12, price_tiers: [], research_areas: [],
};

function ProductForm({ open, initial, onClose, onSaved }) {
  const isEdit = Boolean(initial?.slug);
  const [form, setForm] = useState(EMPTY_PRODUCT);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setForm(initial ? { ...EMPTY_PRODUCT, ...initial } : EMPTY_PRODUCT);
  }, [initial, open]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target ? e.target.value : e }));

  const submit = async () => {
    setSaving(true);
    try {
      const payload = {
        ...form,
        price: Number(form.price) || 0,
        stock: Number(form.stock) || 0,
        research_areas: Array.isArray(form.research_areas) ? form.research_areas : String(form.research_areas || "").split(",").map((s) => s.trim()).filter(Boolean),
        price_tiers: Array.isArray(form.price_tiers) ? form.price_tiers : [],
      };
      if (isEdit) {
        await api.put(`/admin/products/${initial.slug}`, payload);
        toast.success("Product updated");
      } else {
        await api.post("/admin/products", payload);
        toast.success("Product created");
      }
      onSaved();
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to save");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl bg-[#1F1F1F] border-white/10 text-[#F5F5F5] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl">{isEdit ? "Edit Product" : "New Product"}</DialogTitle>
        </DialogHeader>
        <div className="grid grid-cols-2 gap-4 mt-2">
          <div>
            <label className="text-xs text-[#A8A8A8] uppercase tracking-widest">Slug</label>
            <Input data-testid="product-form-slug" value={form.slug} onChange={set("slug")} disabled={isEdit} placeholder="bpc-157" />
          </div>
          <div>
            <label className="text-xs text-[#A8A8A8] uppercase tracking-widest">Name</label>
            <Input data-testid="product-form-name" value={form.name} onChange={set("name")} placeholder="BPC-157" />
          </div>
          <div>
            <label className="text-xs text-[#A8A8A8] uppercase tracking-widest">Category</label>
            <Input data-testid="product-form-category" value={form.category} onChange={set("category")} placeholder="Recovery" />
          </div>
          <div>
            <label className="text-xs text-[#A8A8A8] uppercase tracking-widest">Size</label>
            <Input data-testid="product-form-size" value={form.size} onChange={set("size")} placeholder="5mg / vial" />
          </div>
          <div>
            <label className="text-xs text-[#A8A8A8] uppercase tracking-widest">Price (USD)</label>
            <Input data-testid="product-form-price" type="number" step="0.01" value={form.price} onChange={set("price")} />
          </div>
          <div>
            <label className="text-xs text-[#A8A8A8] uppercase tracking-widest">Stock</label>
            <Input data-testid="product-form-stock" type="number" value={form.stock} onChange={set("stock")} />
          </div>
          <div className="col-span-2">
            <label className="text-xs text-[#A8A8A8] uppercase tracking-widest">Tagline</label>
            <Input data-testid="product-form-tagline" value={form.tagline} onChange={set("tagline")} />
          </div>
          <div className="col-span-2">
            <label className="text-xs text-[#A8A8A8] uppercase tracking-widest">Image URL</label>
            <Input data-testid="product-form-image" value={form.image} onChange={set("image")} />
          </div>
          <div className="col-span-2">
            <label className="text-xs text-[#A8A8A8] uppercase tracking-widest">Description</label>
            <Textarea data-testid="product-form-description" rows={3} value={form.description} onChange={set("description")} />
          </div>
          <div>
            <label className="text-xs text-[#A8A8A8] uppercase tracking-widest">Sequence</label>
            <Input value={form.sequence} onChange={set("sequence")} />
          </div>
          <div>
            <label className="text-xs text-[#A8A8A8] uppercase tracking-widest">Molecular Weight</label>
            <Input value={form.molecular_weight} onChange={set("molecular_weight")} />
          </div>
          <div>
            <label className="text-xs text-[#A8A8A8] uppercase tracking-widest">Purity</label>
            <Input value={form.purity} onChange={set("purity")} placeholder="99.2%" />
          </div>
          <div className="flex items-end gap-3">
            <div className="flex-1">
              <label className="text-xs text-[#A8A8A8] uppercase tracking-widest block mb-2">Featured</label>
              <Switch data-testid="product-form-featured" checked={!!form.featured} onCheckedChange={(v) => setForm((f) => ({ ...f, featured: v }))} />
            </div>
          </div>
        </div>
        <DialogFooter className="mt-4">
          <Button variant="ghost" onClick={onClose} disabled={saving}>Cancel</Button>
          <Button data-testid="product-form-save" onClick={submit} disabled={saving || !form.slug || !form.name} className="bg-[#4ADE80] text-[#0F0F10] hover:bg-[#6FE998]">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : (isEdit ? "Save Changes" : "Create Product")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ProductsTab({ products, loading, reload }) {
  const [editing, setEditing] = useState(null);
  const [creating, setCreating] = useState(false);
  const [toDelete, setToDelete] = useState(null);
  const [importBusy, setImportBusy] = useState(false);
  const fileInputRef = useRef(null);

  const del = async () => {
    try {
      await api.delete(`/admin/products/${toDelete.slug}`);
      toast.success("Product deleted");
      setToDelete(null);
      reload();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Delete failed");
    }
  };

  const exportCsv = async () => {
    try {
      const res = await api.get("/admin/products/export.csv", { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = "endag1-products.csv";
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("Exported CSV");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Export failed");
    }
  };

  const importCsv = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setImportBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/admin/products/import", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const parts = [];
      if (data.inserted) parts.push(`${data.inserted} inserted`);
      if (data.updated) parts.push(`${data.updated} updated`);
      if (data.errors?.length) parts.push(`${data.errors.length} errors`);
      toast.success("Import complete — " + (parts.join(" · ") || "no changes"));
      if (data.errors?.length) console.warn("CSV import errors:", data.errors);
      reload();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Import failed");
    } finally {
      setImportBusy(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  if (loading) return <div className="flex justify-center py-16"><Loader2 className="animate-spin text-[#4ADE80]" /></div>;

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap justify-between items-center gap-3">
        <div className="text-sm text-[#A8A8A8]">{products.length} products</div>
        <div className="flex flex-wrap gap-2">
          <Button
            data-testid="admin-export-csv-btn"
            onClick={exportCsv}
            variant="ghost"
            className="border border-white/20 text-[#F5F5F5] hover:border-[#4ADE80] hover:text-[#4ADE80]"
          >
            <Download className="h-4 w-4 mr-2" /> Export CSV
          </Button>
          <Button
            data-testid="admin-import-csv-btn"
            onClick={() => fileInputRef.current?.click()}
            disabled={importBusy}
            variant="ghost"
            className="border border-white/20 text-[#F5F5F5] hover:border-[#4ADE80] hover:text-[#4ADE80]"
          >
            {importBusy ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Upload className="h-4 w-4 mr-2" />}
            {importBusy ? "Importing…" : "Import CSV"}
          </Button>
          <input
            data-testid="admin-import-csv-input"
            ref={fileInputRef}
            type="file"
            accept=".csv,text/csv"
            onChange={importCsv}
            className="hidden"
          />
          <Button data-testid="admin-new-product-btn" onClick={() => setCreating(true)} className="bg-[#4ADE80] text-[#0F0F10] hover:bg-[#6FE998]">
            <Plus className="h-4 w-4 mr-2" /> New Product
          </Button>
        </div>
      </div>
      <div className="border border-white/10 bg-[#1F1F1F] overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="text-[10px] uppercase tracking-[0.2em] text-[#A8A8A8]">
            <tr className="border-b border-white/10">
              <th className="text-left px-4 py-3">Product</th>
              <th className="text-left px-4 py-3">Category</th>
              <th className="text-right px-4 py-3">Price</th>
              <th className="text-right px-4 py-3">Stock</th>
              <th className="text-center px-4 py-3">Featured</th>
              <th className="text-right px-4 py-3">Actions</th>
            </tr>
          </thead>
          <tbody>
            {products.map((p) => (
              <tr key={p.slug} data-testid={`admin-product-row-${p.slug}`} className={`border-b border-white/5 hover:bg-white/[0.02] ${p.archived ? "opacity-50" : ""}`}>
                <td className="px-4 py-3">
                  <div className="text-[#F5F5F5] flex items-center gap-2">
                    {p.name}
                    {p.archived && <Badge className="bg-yellow-500/20 text-yellow-400 border-0 text-[9px]">Archived</Badge>}
                  </div>
                  <div className="text-xs text-[#A8A8A8]">{p.slug}</div>
                </td>
                <td className="px-4 py-3 text-[#A8A8A8]">{p.category}</td>
                <td className="px-4 py-3 text-right font-mono text-[#F5F5F5]">{fmtPrice(p.price)}</td>
                <td className={`px-4 py-3 text-right font-mono ${p.stock === 0 ? "text-red-400" : p.stock <= 5 ? "text-[#4ADE80]" : "text-[#F5F5F5]"}`}>{p.stock}</td>
                <td className="px-4 py-3 text-center">{p.featured ? <Badge className="bg-[#4ADE80]/20 text-[#4ADE80] border-0">Yes</Badge> : <span className="text-[#A8A8A8]">—</span>}</td>
                <td className="px-4 py-3 text-right">
                  <div className="inline-flex gap-2">
                    <Button data-testid={`admin-edit-${p.slug}`} size="sm" variant="ghost" onClick={() => setEditing(p)}><Pencil className="h-4 w-4" /></Button>
                    <Button
                      data-testid={`admin-archive-${p.slug}`}
                      size="sm"
                      variant="ghost"
                      title={p.archived ? "Unarchive (show in storefront)" : "Archive (hide from storefront)"}
                      onClick={async () => {
                        try {
                          await api.post(`/admin/products/${p.slug}/${p.archived ? "unarchive" : "archive"}`);
                          toast.success(p.archived ? "Product restored" : "Product archived");
                          reload();
                        } catch (e) {
                          toast.error(e.response?.data?.detail || "Failed");
                        }
                      }}
                    >
                      {p.archived ? <Package className="h-4 w-4 text-yellow-400" /> : <Package className="h-4 w-4" />}
                    </Button>
                    <Button data-testid={`admin-delete-${p.slug}`} size="sm" variant="ghost" onClick={() => setToDelete(p)}><Trash2 className="h-4 w-4 text-red-400" /></Button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <ProductForm open={creating} initial={null} onClose={() => setCreating(false)} onSaved={reload} />
      <ProductForm open={!!editing} initial={editing} onClose={() => setEditing(null)} onSaved={reload} />

      <AlertDialog open={!!toDelete} onOpenChange={(o) => !o && setToDelete(null)}>
        <AlertDialogContent className="bg-[#1F1F1F] border-white/10 text-[#F5F5F5]">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete {toDelete?.name}?</AlertDialogTitle>
            <AlertDialogDescription className="text-[#A8A8A8]">
              This will permanently remove the product from the catalog. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="bg-transparent border border-white/20 text-[#F5F5F5] hover:bg-white/5">Cancel</AlertDialogCancel>
            <AlertDialogAction data-testid="admin-confirm-delete" onClick={del} className="bg-red-500/80 hover:bg-red-500 text-white">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function AddressBlock({ addr, name, phone, order, onPrint }) {
  if (!addr && !name) {
    return <div className="text-xs text-[#7a7a7a] italic">No shipping address on file</div>;
  }
  const parts = [
    addr?.street,
    addr?.apartment,
    [addr?.city, addr?.state].filter(Boolean).join(", "),
    [addr?.postal_code, addr?.country].filter(Boolean).join(" "),
  ].filter(Boolean);
  const oneLine = parts.filter(Boolean).join(", ");

  const copy = async () => {
    const block = [name, addr?.street, addr?.apartment, [addr?.city, addr?.state, addr?.postal_code].filter(Boolean).join(", "), addr?.country, phone].filter(Boolean).join("\n");
    try { await navigator.clipboard.writeText(block); toast.success("Shipping address copied"); }
    catch { toast.error("Copy failed"); }
  };

  return (
    <div data-testid="admin-order-shipping" className="space-y-1">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="text-[10px] uppercase tracking-[0.16em] text-[#4ADE80]">Ship to</div>
        {(addr || name) && (
          <div className="flex items-center gap-3">
            <button data-testid="admin-order-copy-address" onClick={copy} className="text-[10px] uppercase tracking-[0.16em] text-[#A8A8A8] hover:text-[#4ADE80]">Copy</button>
            {onPrint && (
              <button
                data-testid="admin-order-print-label"
                onClick={onPrint}
                className="inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.16em] text-[#0F0F10] bg-[#4ADE80] hover:bg-[#6FE998] px-2 py-1"
              >
                <Printer className="h-3 w-3" /> Print label
              </button>
            )}
          </div>
        )}
      </div>
      {(name || addr?.full_name) && <div className="text-sm text-[#F5F5F5]">{name || addr.full_name}</div>}
      {parts.map((p, i) => <div key={i} className="text-sm text-[#A8A8A8]">{p}</div>)}
      {phone && <div className="text-sm text-[#A8A8A8]">📞 {phone}</div>}
      {!addr && <div className="text-xs text-[#7a7a7a] italic">No detailed address — {oneLine || "not provided"}</div>}
    </div>
  );
}


function OrdersTab({ orders, loading, reload }) {
  const [saving, setSaving] = useState(null);
  const [tracking, setTracking] = useState({});
  const [expanded, setExpanded] = useState(null);

  const updateStatus = async (order, status) => {
    setSaving(order.session_id);
    try {
      await api.put(`/admin/orders/${order.session_id}/status`, {
        fulfillment_status: status,
        tracking_number: tracking[order.session_id] ?? order.tracking_number ?? null,
      });
      toast.success(`Order marked ${status}`);
      reload();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to update");
    } finally {
      setSaving(null);
    }
  };

  if (loading) return <div className="flex justify-center py-16"><Loader2 className="animate-spin text-[#4ADE80]" /></div>;

  const unshipped = orders.filter((o) => {
    const s = (o.fulfillment_status || "pending").toLowerCase();
    return ["pending", "processing"].includes(s) && (o.payment_status === "paid" || o.method === "etransfer") && (o.shipping_address || o.name);
  });

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="text-sm text-[#A8A8A8]">{orders.length} orders · click any row to view shipping details</div>
        <Button
          data-testid="admin-print-all-labels"
          onClick={() => openPrintAllLabels(unshipped)}
          disabled={unshipped.length === 0}
          className="bg-[#4ADE80] text-[#0F0F10] hover:bg-[#6FE998] disabled:opacity-40"
        >
          <Printer className="h-4 w-4 mr-2" /> Print all unshipped labels ({unshipped.length})
        </Button>
      </div>
      <div className="border border-white/10 bg-[#1F1F1F] overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="text-[10px] uppercase tracking-[0.2em] text-[#A8A8A8]">
            <tr className="border-b border-white/10">
              <th className="text-left px-4 py-3">Order</th>
              <th className="text-left px-4 py-3">Customer</th>
              <th className="text-right px-4 py-3">Amount</th>
              <th className="text-left px-4 py-3">Payment</th>
              <th className="text-left px-4 py-3">Fulfillment</th>
              <th className="text-left px-4 py-3">Tracking</th>
            </tr>
          </thead>
          <tbody>
            {orders.map((o) => {
              const status = o.fulfillment_status || "pending";
              const isOpen = expanded === o.session_id;
              return (
                <React.Fragment key={o.session_id}>
                  <tr
                    data-testid={`admin-order-row-${o.session_id}`}
                    onClick={() => setExpanded(isOpen ? null : o.session_id)}
                    className={`border-b border-white/5 hover:bg-white/[0.02] cursor-pointer ${isOpen ? "bg-white/[0.03]" : ""}`}
                  >
                    <td className="px-4 py-3">
                      <div className="font-mono text-xs text-[#F5F5F5]">{(o.session_id || "").slice(0, 14)}...</div>
                      <div className="text-[10px] text-[#A8A8A8]">{(o.created_at || "").slice(0, 10)}</div>
                      <div className="text-[10px] text-[#A8A8A8] mt-1">{(o.items || []).join(", ")}</div>
                    </td>
                    <td className="px-4 py-3 text-[#A8A8A8]">
                      <div>{o.name || "—"}</div>
                      <div className="text-[10px] text-[#7a7a7a]">{o.email || "—"}</div>
                    </td>
                    <td className="px-4 py-3 text-right font-mono text-[#F5F5F5]">
                      {Number(o.amount || 0).toFixed(2)} <span className="text-xs text-[#A8A8A8]">{(o.currency || "usd").toUpperCase()}</span>
                    </td>
                    <td className="px-4 py-3">
                      <Badge className={`${o.payment_status === "paid" ? "bg-emerald-500/20 text-emerald-300" : "bg-white/10 text-[#A8A8A8]"} border-0`}>{o.payment_status || "—"}</Badge>
                    </td>
                    <td className="px-4 py-3" onClick={(e) => e.stopPropagation()}>
                      <Select value={status} onValueChange={(v) => updateStatus(o, v)} disabled={saving === o.session_id}>
                        <SelectTrigger data-testid={`admin-order-status-${o.session_id}`} className="w-36 bg-transparent border-white/20 text-[#F5F5F5]">
                          <SelectValue><span className={`text-xs px-2 py-0.5 rounded ${STATUS_COLOR[status] || ""}`}>{status}</span></SelectValue>
                        </SelectTrigger>
                        <SelectContent className="bg-[#1F1F1F] border-white/10 text-[#F5F5F5]">
                          {STATUS_ORDER.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </td>
                    <td className="px-4 py-3" onClick={(e) => e.stopPropagation()}>
                      <Input
                        data-testid={`admin-order-tracking-${o.session_id}`}
                        className="w-40 bg-transparent border-white/20 text-xs"
                        placeholder="Tracking #"
                        defaultValue={o.tracking_number || ""}
                        onChange={(e) => setTracking((t) => ({ ...t, [o.session_id]: e.target.value }))}
                        onBlur={(e) => {
                          if ((e.target.value || "") !== (o.tracking_number || "")) {
                            updateStatus(o, status);
                          }
                        }}
                      />
                    </td>
                  </tr>
                  {isOpen && (
                    <tr data-testid={`admin-order-expanded-${o.session_id}`} className="border-b border-white/10 bg-[#171717]">
                      <td colSpan={6} className="px-6 py-5">
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                          <div className="md:col-span-1 border border-white/10 p-4">
                            <AddressBlock
                              addr={o.shipping_address}
                              name={o.name}
                              phone={o.phone}
                              order={o}
                              onPrint={() => openPrintLabel(o)}
                            />
                          </div>
                          <div className="md:col-span-2 border border-white/10 p-4">
                            <div className="text-[10px] uppercase tracking-[0.16em] text-[#4ADE80] mb-2">Order details</div>
                            <div className="grid grid-cols-2 gap-2 text-sm">
                              <div className="text-[#A8A8A8]">Session ID</div><div className="font-mono text-xs text-[#F5F5F5] break-all">{o.session_id}</div>
                              <div className="text-[#A8A8A8]">Method</div><div className="text-[#F5F5F5]">{o.method || "card"}</div>
                              <div className="text-[#A8A8A8]">Subtotal</div><div className="font-mono text-[#F5F5F5]">${(o.subtotal || o.amount || 0).toFixed(2)}</div>
                              {o.discount_amount ? (
                                <>
                                  <div className="text-[#A8A8A8]">Discount ({o.discount_code || ""})</div>
                                  <div className="font-mono text-[#4ADE80]">−${Number(o.discount_amount).toFixed(2)}</div>
                                </>
                              ) : null}
                              <div className="text-[#A8A8A8]">Total charged</div><div className="font-mono text-[#F5F5F5] font-semibold">${Number(o.amount || 0).toFixed(2)} {(o.currency || "usd").toUpperCase()}</div>
                              {o.ref_code && (
                                <>
                                  <div className="text-[#A8A8A8]">Referred by</div>
                                  <div className="font-mono text-[#4ADE80]">{o.ref_code}</div>
                                </>
                              )}
                            </div>
                            {Array.isArray(o.line_items) && o.line_items.length > 0 && (
                              <div className="mt-4 pt-4 border-t border-white/10">
                                <div className="text-[10px] uppercase tracking-[0.16em] text-[#4ADE80] mb-2">Items</div>
                                <ul className="text-sm text-[#F5F5F5] space-y-1">
                                  {o.line_items.map((li, i) => (
                                    <li key={i} className="flex justify-between">
                                      <span>{li.name}</span>
                                      <span className="text-[#A8A8A8]">× {li.quantity}</span>
                                    </li>
                                  ))}
                                </ul>
                              </div>
                            )}
                          </div>
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default function Admin() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState("dashboard");
  const [stats, setStats] = useState(null);
  const [products, setProducts] = useState([]);
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState({ stats: true, products: true, orders: true });

  const loadStats = useCallback(async () => {
    setLoading((l) => ({ ...l, stats: true }));
    try {
      const { data } = await api.get("/admin/stats");
      setStats(data);
    } finally { setLoading((l) => ({ ...l, stats: false })); }
  }, []);

  const loadProducts = useCallback(async () => {
    setLoading((l) => ({ ...l, products: true }));
    try {
      const { data } = await api.get("/admin/products");
      setProducts(data);
    } finally { setLoading((l) => ({ ...l, products: false })); }
  }, []);

  const loadOrders = useCallback(async () => {
    setLoading((l) => ({ ...l, orders: true }));
    try {
      const { data } = await api.get("/admin/orders");
      setOrders(data);
    } finally { setLoading((l) => ({ ...l, orders: false })); }
  }, []);

  useEffect(() => {
    if (user === null) return; // still checking
    if (!user) { navigate("/login"); return; }
    if (!user.is_admin) { navigate("/account"); return; }
    loadStats(); loadProducts(); loadOrders();
  }, [user, navigate, loadStats, loadProducts, loadOrders]);

  if (user === null) {
    return <div className="flex justify-center py-32"><Loader2 className="animate-spin text-[#4ADE80]" /></div>;
  }
  if (!user || !user.is_admin) return null;

  return (
    <div className="min-h-screen bg-[#0F0F10] text-[#F5F5F5]">
      <motion.section
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
        className="mx-auto max-w-[1500px] px-6 sm:px-10 pt-28 pb-16"
      >
        <div className="flex items-center gap-3 mb-2">
          <span className="text-[10px] uppercase tracking-[0.28em] text-[#4ADE80]">Admin</span>
          <span className="h-px w-16 bg-[#4ADE80]/40" />
        </div>
        <h1 className="font-display text-4xl sm:text-5xl tracking-tight">Control Room</h1>
        <p className="mt-2 text-sm text-[#A8A8A8]">Signed in as <span className="text-[#F5F5F5]">{user.email}</span></p>

        <Tabs value={tab} onValueChange={setTab} className="mt-10">
          <TabsList data-testid="admin-tabs" className="bg-[#1F1F1F] border border-white/10">
            <TabsTrigger data-testid="tab-dashboard" value="dashboard" className="data-[state=active]:bg-[#4ADE80] data-[state=active]:text-[#0F0F10]">
              <LayoutDashboard className="h-4 w-4 mr-2" /> Dashboard
            </TabsTrigger>
            <TabsTrigger data-testid="tab-products" value="products" className="data-[state=active]:bg-[#4ADE80] data-[state=active]:text-[#0F0F10]">
              <Package className="h-4 w-4 mr-2" /> Products
            </TabsTrigger>
            <TabsTrigger data-testid="tab-orders" value="orders" className="data-[state=active]:bg-[#4ADE80] data-[state=active]:text-[#0F0F10]">
              <Receipt className="h-4 w-4 mr-2" /> Orders
            </TabsTrigger>
            <TabsTrigger data-testid="tab-discounts" value="discounts" className="data-[state=active]:bg-[#4ADE80] data-[state=active]:text-[#0F0F10]">
              <Tag className="h-4 w-4 mr-2" /> Discounts
            </TabsTrigger>
            <TabsTrigger data-testid="tab-announcement" value="announcement" className="data-[state=active]:bg-[#4ADE80] data-[state=active]:text-[#0F0F10]">
              <Megaphone className="h-4 w-4 mr-2" /> Banner
            </TabsTrigger>
            <TabsTrigger data-testid="tab-partners" value="partners" className="data-[state=active]:bg-[#4ADE80] data-[state=active]:text-[#0F0F10]">
              <Trophy className="h-4 w-4 mr-2" /> Partners
            </TabsTrigger>
          </TabsList>

          <TabsContent value="dashboard" className="mt-8">
            <DashboardTab stats={stats} loading={loading.stats} />
          </TabsContent>
          <TabsContent value="products" className="mt-8">
            <ProductsTab products={products} loading={loading.products} reload={loadProducts} />
          </TabsContent>
          <TabsContent value="orders" className="mt-8">
            <OrdersTab orders={orders} loading={loading.orders} reload={loadOrders} />
          </TabsContent>
          <TabsContent value="discounts" className="mt-8">
            <DiscountsTab />
          </TabsContent>
          <TabsContent value="announcement" className="mt-8">
            <AnnouncementTab />
          </TabsContent>
          <TabsContent value="partners" className="mt-8">
            <PartnersTab />
          </TabsContent>
        </Tabs>
      </motion.section>
    </div>
  );
}
